import { createCrudService } from '../api/baseService';
import easyCheckApi, { easyCheckRequest } from '../api/config';

class ChequeTemplateService {
  constructor() {
    this.crudService = createCrudService('cheque-templates');
  }

  // Get template by bank ID
  async getTemplateByBankId(bankId) {
    try {
      console.log(`🔍 Getting template for bank ID: ${bankId}`);
      const response = await easyCheckRequest(() => 
        easyCheckApi.get(`/cheque-templates/bank/${bankId}`)
      );
      console.log('✅ Full API response:', response);
      
      // Handle different response structures
      let templateData = response.data;
      if (response.data && response.data.data) {
        templateData = response.data.data;
      }
      
      console.log('✅ Template data extracted:', templateData);
      return templateData;
    } catch (error) {
      if (error.response?.status === 404) {
        console.log('ℹ️ No template found for this bank');
        return null;
      }
      console.error('❌ Error getting template:', error);
      throw error;
    }
  }

  // Create new template
  async createTemplate(templateData) {
    try {
      console.log('📝 Creating new template:', templateData);
      const response = await this.crudService.create(templateData);
      console.log('✅ Template created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating template:', error);
      throw error;
    }
  }

  // Update existing template
  async updateTemplate(templateId, templateData) {
    try {
      console.log(`📝 Updating template ${templateId}:`, templateData);
      const response = await this.crudService.update(templateId, templateData);
      console.log('✅ Template updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating template:', error);
      throw error;
    }
  }

  // Delete template
  async deleteTemplate(templateId) {
    try {
      console.log(`🗑️ Deleting template ${templateId}`);
      const response = await this.crudService.delete(templateId);
      console.log('✅ Template deleted successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting template:', error);
      throw error;
    }
  }

  // Save or update template (creates if doesn't exist, updates if exists)
  async saveTemplate(templateData) {
    try {
      const { bankId } = templateData;
      console.log('💾 Saving template with data:', templateData);
      
      // Convert camelCase field names to PascalCase for API compatibility
      const apiTemplateData = {
        BankId: bankId,
        TemplateHeight: templateData.templateHeight,
        TemplateWidth: templateData.templateWidth,
        BeneficiaryLeft: templateData.beneficiaryLeft,
        BeneficiaryTop: templateData.beneficiaryTop,
        BeneficiaryWidth: templateData.beneficiaryWidth,
        Beneficiary2Left: templateData.beneficiary2Left,
        Beneficiary2Top: templateData.beneficiary2Top,
        DateLeft: templateData.dateLeft,
        DateTop: templateData.dateTop,
        AmountLeft: templateData.amountLeft,
        AmountTop: templateData.amountTop,
        AmountInWords1Left: templateData.amountInWords1Left,
        AmountInWords1Top: templateData.amountInWords1Top,
        AmountInWordsWidth: templateData.amountInWordsWidth,
        AmountInWords2Left: templateData.amountInWords2Left,
        AmountInWords2Top: templateData.amountInWords2Top,
        Amount2Width: templateData.amount2Width,
        AccountPayeeLeft: templateData.accountPayeeLeft,
        AccountPayeeTop: templateData.accountPayeeTop,
        RemarksLeft: templateData.remarksLeft,
        RemarksTop: templateData.remarksTop,
        ChequeImageFilename: templateData.chequeImageFilename || null
      };
      
      console.log('💾 Converted API data:', apiTemplateData);
      
      // First check if template exists for this bank
      const existingTemplate = await this.getTemplateByBankId(bankId);
      
      if (existingTemplate && existingTemplate.Id) {
        // Update existing template
        console.log('📝 Updating existing template with ID:', existingTemplate.Id);
        const response = await easyCheckRequest(() => 
          easyCheckApi.put(`/cheque-templates/${existingTemplate.Id}`, apiTemplateData)
        );
        console.log('✅ Template updated successfully:', response.data);
        return response.data;
      } else {
        // Create new template
        console.log('📝 Creating new template');
        const response = await easyCheckRequest(() => 
          easyCheckApi.post('/cheque-templates', apiTemplateData)
        );
        console.log('✅ Template created successfully:', response.data);
        return response.data;
      }
    } catch (error) {
      console.error('❌ Error saving template:', error);
      throw error;
    }
  }

  // Check if template is configured for a bank
  isTemplateConfigured(bank) {
    if (!bank) return false;
    
    // This method will be enhanced to work with template objects
    // For now, we'll rely on the BankSetupPage to handle template status
    // by fetching the actual template data via getTemplateByBankId
    return false; // This will trigger template fetching in the calling component
  }

  // Upload a cheque scan image for template detection
  async uploadChequeImage(bankId, imageFile) {
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      formData.append('bankId', bankId);

      const response = await easyCheckRequest(() =>
        easyCheckApi.post('/cheque-textract/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000
        })
      );

      console.log('Upload response:', JSON.stringify(response));

      if (!response.success) {
        throw new Error(response.message || 'Upload failed');
      }

      let data = response.data;
      if (data && data.data) data = data.data;
      if (!data) {
        throw new Error('No data returned from upload');
      }
      return data;
    } catch (error) {
      console.error('Error uploading cheque image:', error);
      throw error;
    }
  }

  // Auto-detect field positions from uploaded cheque image using AWS Textract
  async detectFields(fileName, physicalWidthCm, physicalHeightCm) {
    try {
      const response = await easyCheckRequest(() =>
        easyCheckApi.post('/cheque-textract/detect-fields', {
          FileName: fileName,
          PhysicalWidthCm: physicalWidthCm,
          PhysicalHeightCm: physicalHeightCm
        }, { timeout: 120000 }) // two Claude passes (detect + refine) on the backend
      );

      console.log('Detect response:', JSON.stringify(response));

      if (!response.success) {
        throw new Error(response.message || 'Detection failed');
      }

      let data = response.data;
      if (data && data.data) data = data.data;
      if (!data) {
        throw new Error('No data returned from detection');
      }
      return data;
    } catch (error) {
      console.error('Error detecting fields:', error);
      throw error;
    }
  }

  // Get the URL for a cheque template image
  getImageUrl(fileName) {
    if (!fileName) return null;
    const baseUrl = easyCheckApi.defaults.baseURL;
    return `${baseUrl}/cheque-textract/image/${encodeURIComponent(fileName)}`;
  }

  // Copy a source bank's template to one or more target banks.
  // Returns { successes: [bankId, ...], failures: [{ bankId, message }, ...] }.
  // The backend's POST /cheque-templates already rejects duplicates with
  // TEMPLATE_ALREADY_EXISTS (HTTP 409), so existing templates can never be overwritten.
  async copyTemplateToBanks(sourceBankId, targetBankIds) {
    const source = await this.getTemplateByBankId(sourceBankId);
    if (!source) {
      throw new Error('Source template not found');
    }

    const pick = (pascal, camel) => (source[pascal] !== undefined ? source[pascal] : source[camel]);

    const successes = [];
    const failures = [];

    for (const targetBankId of targetBankIds) {
      const apiPayload = {
        BankId: targetBankId,
        TemplateHeight: pick('TemplateHeight', 'templateHeight'),
        TemplateWidth: pick('TemplateWidth', 'templateWidth'),
        BeneficiaryLeft: pick('BeneficiaryLeft', 'beneficiaryLeft'),
        BeneficiaryTop: pick('BeneficiaryTop', 'beneficiaryTop'),
        BeneficiaryWidth: pick('BeneficiaryWidth', 'beneficiaryWidth'),
        Beneficiary2Left: pick('Beneficiary2Left', 'beneficiary2Left'),
        Beneficiary2Top: pick('Beneficiary2Top', 'beneficiary2Top'),
        DateLeft: pick('DateLeft', 'dateLeft'),
        DateTop: pick('DateTop', 'dateTop'),
        AmountLeft: pick('AmountLeft', 'amountLeft'),
        AmountTop: pick('AmountTop', 'amountTop'),
        AmountInWords1Left: pick('AmountInWords1Left', 'amountInWords1Left'),
        AmountInWords1Top: pick('AmountInWords1Top', 'amountInWords1Top'),
        AmountInWordsWidth: pick('AmountInWordsWidth', 'amountInWordsWidth'),
        AmountInWords2Left: pick('AmountInWords2Left', 'amountInWords2Left'),
        AmountInWords2Top: pick('AmountInWords2Top', 'amountInWords2Top'),
        Amount2Width: pick('Amount2Width', 'amount2Width'),
        AccountPayeeLeft: pick('AccountPayeeLeft', 'accountPayeeLeft'),
        AccountPayeeTop: pick('AccountPayeeTop', 'accountPayeeTop'),
        RemarksLeft: pick('RemarksLeft', 'remarksLeft'),
        RemarksTop: pick('RemarksTop', 'remarksTop'),
        ChequeImageFilename: pick('ChequeImageFilename', 'chequeImageFilename') || null,
      };

      try {
        await easyCheckRequest(() => easyCheckApi.post('/cheque-templates', apiPayload));
        successes.push(targetBankId);
      } catch (error) {
        const message =
          error?.response?.data?.Message ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to copy template';
        failures.push({ bankId: targetBankId, message });
      }
    }

    return { successes, failures };
  }

  // Search global template library (for import)
  async searchLibraryTemplates(params = {}) {
    try {
      const response = await easyCheckRequest(() =>
        easyCheckApi.get('/cheque-templates/library', { params })
      );
      return response;
    } catch (error) {
      console.error('Error searching template library:', error);
      throw error;
    }
  }

  // Template version history (Bank Backup module) — snapshots newest first
  async getTemplateHistory(bankId) {
    const response = await easyCheckRequest(() =>
      easyCheckApi.get(`/cheque-templates/history/bank/${bankId}`)
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to load template history');
    }
    let data = response.data;
    if (data && data.data) data = data.data;
    return Array.isArray(data) ? data : [];
  }

  // Restore a snapshot; the backend snapshots the current template first (undoable)
  async restoreTemplateVersion(historyId) {
    const response = await easyCheckRequest(() =>
      easyCheckApi.post(`/cheque-templates/history/${historyId}/restore`)
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to restore template version');
    }
    return response.data;
  }

  // New method to check if a template object is properly configured
  isTemplateObjectConfigured(template) {
    if (!template) return false;
    
    // Check if essential template fields are filled
    const essentialFields = [
      'templateHeight', 'templateWidth',
      'beneficiaryLeft', 'beneficiaryTop', 'beneficiaryWidth',
      'beneficiary2Left', 'beneficiary2Top',
      'dateLeft', 'dateTop',
      'amountLeft', 'amountTop',
      'amountInWords1Left', 'amountInWords1Top', 'amountInWordsWidth',
      'amountInWords2Left', 'amountInWords2Top', 'amount2Width',
      'accountPayeeLeft', 'accountPayeeTop',
      'remarksLeft', 'remarksTop'
    ];
    
    return essentialFields.every(field => 
      template[field] !== null && 
      template[field] !== undefined && 
      template[field] !== '' &&
      template[field] !== 0
    );
  }
}

export const chequeTemplateService = new ChequeTemplateService(); 